import { logger } from "@/utils/logger";
/**
 * Error handling utilities for API responses
 * Handles structured error responses from the backend API
 */

interface ApiErrorData {
  httpStatusCode?: string | number;
  errorCode?: string;
  errors?: Array<Record<string, unknown>>;
  defaultUserMessage?: string;
  developerMessage?: string;
  message?: string;
  response?: {
    errorCode?: string;
    errorText?: string;
    status?: number;
  };
}

interface AxiosError {
  response?: {
    data?: ApiErrorData;
    status?: number;
    errorText?: string;
    errorCode?: string;
  };
  message?: string;
}

/**
 * Extract error information from API response
 * @param {Object} error - The error object from API call
 * @returns {Object} - Structured error information
 */
export function extractApiError(error: unknown): Record<string, unknown> {
  // If it's already a structured error response
  if (error && typeof error === "object") {
    const errorObj = error as ApiErrorData;
    if (errorObj.httpStatusCode) {
      return errorObj as Record<string, unknown>;
    }
  }

  // If it's an axios error with response data
  const axiosError = error as AxiosError;
  if (axiosError.response) {
    const responseData = axiosError.response.data;

    if (responseData) {
      // Check if it's a structured error response
      if (
        responseData.httpStatusCode ||
        responseData.errors ||
        responseData.defaultUserMessage
      ) {
        if (responseData.response?.errorCode && !responseData.errorCode) {
          responseData.errorCode = responseData.response.errorCode;
        }
        return responseData as Record<string, unknown>;
      }

      // Handle nested response payloads like { response: { errorText } }
      if (
        responseData.response?.errorText ||
        responseData.response?.errorCode
      ) {
        const innerResponse = responseData.response;
        const errorText = innerResponse.errorText || "An error occurred";
        const developerMessage = innerResponse.errorCode
          ? `${innerResponse.errorCode}: ${errorText}`
          : errorText;

        return {
          httpStatusCode: axiosError.response.status?.toString() || "500",
          errorCode: innerResponse.errorCode,
          defaultUserMessage: errorText,
          developerMessage,
          errors: [],
        };
      }

      // Fallback to generic error structure for data payloads
      return {
        httpStatusCode: axiosError.response.status?.toString() || "500",
        errorCode: responseData.errorCode,
        defaultUserMessage: responseData.message || "An error occurred",
        developerMessage: responseData.developerMessage || axiosError.message,
        errors: responseData.errors || [],
      };
    }

    // Handle responses where the error is nested directly under response (no data key)
    if (axiosError.response.errorText || axiosError.response.errorCode) {
      const errorText = axiosError.response.errorText || "An error occurred";
      const developerMessage = axiosError.response.errorCode
        ? `${axiosError.response.errorCode}: ${errorText}`
        : errorText;

      return {
        httpStatusCode: axiosError.response.status?.toString() || "500",
        errorCode: axiosError.response.errorCode,
        defaultUserMessage: errorText,
        developerMessage,
        errors: [],
      };
    }
  }

  // If it's a network error or other error
  const errorWithMessage = error as { message?: string };
  if (errorWithMessage?.message) {
    return {
      httpStatusCode: "500",
      defaultUserMessage: "Network error or server unavailable",
      developerMessage: errorWithMessage.message,
      errors: [],
    };
  }

  // Fallback for unknown errors
  return {
    httpStatusCode: "500",
    defaultUserMessage: "An unexpected error occurred",
    developerMessage: "Unknown error",
    errors: [],
  };
}

/**
 * Check if an error is a validation error
 * @param {Object} error - The error object
 * @returns {boolean} - True if it's a validation error
 */
export function isValidationError(error: unknown): boolean {
  const extractedError = extractApiError(error);
  return (
    extractedError.httpStatusCode === "400" ||
    (extractedError.errors as Array<unknown> | undefined)?.length! > 0 ||
    (extractedError.defaultUserMessage as string | undefined)
      ?.toLowerCase()
      .includes("validation") ||
    false
  );
}

/**
 * Get user-friendly error message from API error
 * @param {Object} error - The error object
 * @returns {string} - User-friendly error message
 */
export function getUserErrorMessage(error: unknown): string {
  const extractedError = extractApiError(error);
  return (extractedError.defaultUserMessage as string) || "An error occurred";
}

/**
 * Get all individual error messages from API error
 * @param {Object} error - The error object
 * @returns {Array} - Array of individual error messages
 */
export function getIndividualErrorMessages(
  error: unknown,
): Array<Record<string, unknown>> {
  const extractedError = extractApiError(error);
  return (extractedError.errors as Array<Record<string, unknown>>) || [];
}

/**
 * Handle API error in form submission
 * @param {Object} error - The error from API call
 * @param {Function} setError - Function to set error state
 * @param {string} fallbackMessage - Fallback message if error extraction fails
 */
export function handleApiError(
  error: unknown,
  setError: (error: Record<string, unknown>) => void,
  _fallbackMessage: string = "An error occurred",
): void {
  logger.error("API Error:", error);

  const extractedError = extractApiError(error);
  setError(extractedError);
}

/**
 * Create a structured error response for testing or manual error creation
 * @param {string} message - Main error message
 * @param {Array} errors - Array of individual errors
 * @param {string} statusCode - HTTP status code
 * @returns {Object} - Structured error response
 */
export function createStructuredError(
  message: string,
  errors: Array<Record<string, unknown>> = [],
  statusCode: string = "400",
): Record<string, unknown> {
  return {
    httpStatusCode: statusCode,
    defaultUserMessage: message,
    developerMessage: message,
    userMessageGlobalisationCode: "error.msg.general",
    errors: errors.map((err: Record<string, unknown>) => ({
      developerMessage: err.message || err,
      defaultUserMessage: err.message || err,
      userMessageGlobalisationCode: err.code || "error.msg.validation",
      parameterName: err.parameter || "field",
      value: err.value || "",
      args: [],
    })),
  };
}

/**
 * Example usage and common error patterns
 */
export const COMMON_ERROR_PATTERNS = {
  VALIDATION_ERROR: {
    httpStatusCode: "400",
    defaultUserMessage: "Validation errors exist.",
    developerMessage:
      "The request was invalid. This typically will happen due to validation errors which are provided.",
    userMessageGlobalisationCode: "validation.msg.validation.errors.exist",
  },

  NOT_FOUND: {
    httpStatusCode: "404",
    defaultUserMessage: "Resource not found",
    developerMessage: "The requested resource was not found",
    userMessageGlobalisationCode: "error.msg.not.found",
  },

  UNAUTHORIZED: {
    httpStatusCode: "401",
    defaultUserMessage: "Unauthorized access",
    developerMessage: "Authentication required",
    userMessageGlobalisationCode: "error.msg.unauthorized",
  },

  FORBIDDEN: {
    httpStatusCode: "403",
    defaultUserMessage: "Access denied",
    developerMessage: "Insufficient permissions",
    userMessageGlobalisationCode: "error.msg.forbidden",
  },

  SERVER_ERROR: {
    httpStatusCode: "500",
    defaultUserMessage: "Internal server error",
    developerMessage: "An unexpected error occurred on the server",
    userMessageGlobalisationCode: "error.msg.server.error",
  },
};

export default {
  extractApiError,
  isValidationError,
  getUserErrorMessage,
  getIndividualErrorMessages,
  handleApiError,
  createStructuredError,
  COMMON_ERROR_PATTERNS,
};
